import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
const GoogleButton = ({ role }) => {
  const onClick = () => {
    if (role) localStorage.setItem("ashland_role_hint", role);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" onClick={onClick} data-testid="google-login-btn" className="w-full h-11 border-slate-300 gap-2">
      <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/><path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75z"/></svg>
      Continue with Google
    </Button>
  );
};

const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen flex flex-col bg-background">
    <Header />
    <div className="flex-1 grid-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-lg p-8">
        <div className="flex justify-center mb-6"><img src="/safety-jobs-logo.png" alt="Safety Jobs" className="h-14 w-14 rounded-xl shadow-sm" /></div>
        <h1 className="font-display text-2xl font-extrabold text-navy-800 text-center">{title}</h1>
        <p className="text-slate-500 text-sm text-center mt-1 mb-6">{subtitle}</p>
        {children}
      </div>
    </div>
    <Footer />
  </div>
);

export function Login() {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [loading, setLoading] = useState(false);
  const { setUser } = useAuth(); const navigate = useNavigate(); const location = useLocation();
  const redirect = location.state?.redirect;

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data); toast.success("Welcome back!");
      navigate(redirect || (data.role === "admin" ? "/admin" : data.role === "employer" ? "/employer" : "/applicant"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Sign in" subtitle="Access your Safety Jobs account">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Email</Label><Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" /></div>
        <div><div className="flex justify-between"><Label>Password</Label><Link to="/forgot-password" className="text-xs text-amber-600 hover:underline">Forgot password?</Link></div><Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" /></div>
        <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11 bg-navy-800 hover:bg-navy-900 text-white">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}</Button>
      </form>
      <div className="flex items-center gap-3 my-5"><div className="h-px flex-1 bg-slate-200" /><span className="text-xs text-slate-400">or</span><div className="h-px flex-1 bg-slate-200" /></div>
      <GoogleButton />
      <p className="text-sm text-center text-slate-500 mt-6">New here? <Link to="/register" className="text-amber-600 font-semibold hover:underline">Create an account</Link></p>
    </AuthShell>
  );
}

export function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "applicant" });
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth(); const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data); toast.success("Account created!");
      navigate(data.role === "employer" ? "/employer" : "/applicant");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Create your account" subtitle="Join the U.S. safety careers marketplace">
      <div className="grid grid-cols-2 gap-2 mb-5">
        {[["applicant", "I'm looking for a job"], ["employer", "I'm hiring"]].map(([val, label]) => (
          <button key={val} type="button" data-testid={`role-${val}`} onClick={() => setForm({ ...form, role: val })}
            className={`p-3 rounded-lg border text-sm font-semibold transition-colors ${form.role === val ? "border-amber-500 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
            {label}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Full name</Label><Input data-testid="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" /></div>
        <div><Label>Email</Label><Input data-testid="register-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" /></div>
        <div><Label>Password</Label><Input data-testid="register-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className="mt-1.5" /></div>
        <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full h-11 bg-navy-800 hover:bg-navy-900 text-white">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}</Button>
      </form>
      <div className="flex items-center gap-3 my-5"><div className="h-px flex-1 bg-slate-200" /><span className="text-xs text-slate-400">or</span><div className="h-px flex-1 bg-slate-200" /></div>
      <GoogleButton role={form.role} />
      <p className="text-sm text-center text-slate-500 mt-6">Already have an account? <Link to="/login" className="text-amber-600 font-semibold hover:underline">Sign in</Link></p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState(""); const [sent, setSent] = useState(false); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <AuthShell title="Reset password" subtitle="We'll email you a reset link">
      {sent ? (
        <div className="text-center">
          <p className="text-sm text-slate-600">If that email is registered, a reset link has been sent. Check your inbox.</p>
          <Link to="/login" className="inline-block mt-6 text-amber-600 font-semibold hover:underline">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Email</Label><Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" /></div>
          <Button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full h-11 bg-navy-800 hover:bg-navy-900 text-white">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}</Button>
          <Link to="/login" className="block text-center text-sm text-slate-500 hover:underline">Back to sign in</Link>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [password, setPassword] = useState(""); const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token");
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/auth/reset-password", { token, password }); toast.success("Password updated. Please sign in."); navigate("/login"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <AuthShell title="Choose a new password" subtitle="Enter your new password below">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>New password</Label><Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5" /></div>
        <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full h-11 bg-navy-800 hover:bg-navy-900 text-white">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}</Button>
        <Link to="/login" className="block text-center text-sm text-slate-500 hover:underline">Back to sign in</Link>
      </form>
    </AuthShell>
  );
}
